import { useMemo } from 'react'
import { useTheme } from '@mui/material'
import { grey } from '@mui/material/colors'
import { animated } from '@react-spring/web'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import { AverageMetricReport } from '@/types/workflow-metric-reports'
import { getChartHeight, getNumberTicks } from '@/utils/chart'
import BarChart from '@/components/charts/BarChart'

type MetricBarChartProps = {
  type: string
  report?: AverageMetricReport
  height?: number
}

const STP_TICK_VALUES = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]

function getIdleTime(value: number, isLabel = false) {
  let time = value / 60 / 60 / 24

  if (!isLabel) return `${time.toFixed(0)} days`

  let timeInHours = false
  if (isLabel && value < 2 * 24 * 60 * 60) {
    time = value / 60 / 60
    timeInHours = true
  }
  return timeInHours ? `${time.toFixed(1)} hrs` : `${time.toFixed(1)} days`
}

export default function AverageMetricBarChart({
  type,
  report,
  height,
}: MetricBarChartProps) {
  const theme = useTheme()
  const getThemeColor = useGetThemeColor()

  const isStp = type === 'doc-stp-average'
  const isIdleTime = type === 'doc_idle_time'

  const reports = report?.reports

  const adjustMarginForStp = useMemo(
    () => isStp && reports?.some((report) => report.formatted_result >= 98),
    [isStp, reports],
  )

  const adjustMarginForIdleTime = useMemo(
    () =>
      isIdleTime &&
      reports?.some((report) => report.formatted_result > 6.72 * 24 * 60 * 60),
    [isIdleTime, reports],
  )

  const adjustMarginForUserTime = useMemo(
    () =>
      !isStp &&
      !isIdleTime &&
      reports?.some((report) => report.formatted_result > 2.9 * 60),
    [isIdleTime, isStp, reports],
  )

  const tickValues = useMemo(() => {
    if (isStp) {
      return STP_TICK_VALUES
    }
    const maxValue = Math.ceil(
      Math.max(
        isIdleTime ? 7 : 3,
        ...(reports || [])?.map((report) =>
          isIdleTime
            ? report.formatted_result / 24 / 60 / 60
            : report.formatted_result / 60,
        ),
      ),
    )
    return getNumberTicks(0, maxValue, 10, true).map((tick) =>
      isIdleTime ? tick * 24 * 60 * 60 : tick * 60,
    )
  }, [isStp, isIdleTime, reports])

  return (
    <BarChart
      height={height || getChartHeight(reports?.length || 0, 200)}
      data={reports || []}
      enableGridY={false}
      indexBy="workflow_state__name"
      isInteractive={false}
      keys={['formatted_result']}
      layout="horizontal"
      padding={0.3}
      axisBottom={{
        format: (value) =>
          isStp
            ? `${value}%`
            : isIdleTime
            ? getIdleTime(value, false)
            : `${Math.ceil(value / 60)} min`,
        tickValues,
      }}
      maxValue={tickValues[tickValues.length - 1]}
      axisLeft={{
        truncateTickAt: 14,
        renderTick: (tick) =>
          reports ? (
            <>
              {/* @ts-expect-error -- it is the right type */}
              <animated.g transform={tick.animatedProps.transform}>
                <rect
                  width="3"
                  height="11"
                  transform="translate(-105,-5)"
                  style={{
                    fill: getThemeColor(
                      reports?.[tick.tickIndex]?.workflow_state__color,
                    ),
                    fontSize: 11,
                  }}
                />
                <text
                  dominantBaseline={tick.textBaseline}
                  transform="translate(-95,0)"
                  style={{
                    fill: theme.palette.text.primary,
                    fontSize: 11,
                  }}
                >
                  {tick.value}
                </text>
              </animated.g>
            </>
          ) : (
            <></>
          ),
      }}
      margin={{
        top: 0,
        bottom: 25,
        left: reports ? 105 : 30,
        right: isStp
          ? adjustMarginForStp
            ? 45
            : 25
          : adjustMarginForIdleTime || adjustMarginForUserTime
          ? 55
          : 30,
      }}
      label={(d) =>
        isStp
          ? `${(d.value || 0).toFixed(1)}%`
          : isIdleTime
          ? getIdleTime(d.value || 0, true)
          : `${((d.value || 0) / 60).toFixed(1)} min`
      }
      labelOffset={4}
      labelPosition="end"
      labelTextColor={theme.palette.text.primary}
      colors={() => {
        if (isStp) {
          return theme.palette.primary.main
        }
        return theme.palette.mode === 'dark'
          ? theme.palette.neutral.main
          : grey[400]
      }}
    />
  )
}
